import { useRouter } from 'next/router';
import clsx from 'clsx';
import { matchSorter } from 'match-sorter';
import useTranslation from 'next-translate/useTranslation';
import type { ComponentPropsWithoutRef } from 'react';

// Hooks
import useLogout from '@/utils/hooks/user/useLogout';
import useUser from '@/utils/hooks/swr/useUser';
import useAcl from '@/utils/hooks/user/useAcl';

// Lib
import settingsMenu from '@/lib/menu/settings';
import type { SettingsMenuEntryType } from '@/lib/menu/settings';
import { ROLES } from '@/lib/user/roles';
import { CHANGE_YOUR_PLAN } from '@/lib/settings/subscriptionInvoices/steps';
import { SOCIAL_PROFILES_ACTION_PARAM } from '@/lib/socialprofiles/queryParams';
import { SOCIAL_PROFILES as SOCIAL_PROFILES_RIGHT } from '@/lib/acl/aclRightsList';

// Routes
import { SOCIAL_PROFILES, SUBSCRIPTION_INVOICES } from '@/routes/privateRoutes';

// Components
import ActionTag from '@/components/base/Tag/ActionTag';
import Icon from '@/components/base/Icon';
import { DefaultAvatar as Avatar } from '@/components/base/Avatar';
import LinkBase from '@/components/base/LinkBase';
import MenuPopover from '@/components/composite/Menu/MenuPopover';
import Button from '@/components/base/Button';

type SettingMenuSubscriptionGuideType = ComponentPropsWithoutRef<'span'> & {
    withBorder?: boolean;
};

const SubscriptionGuide = ({
    withBorder = false,
    className,
    ...restProps
}: SettingMenuSubscriptionGuideType) => {
    return (
        <span
            className={clsx(
                'block rounded-full bg-icoGreen',
                {
                    'h-3 w-3 border-2 border-white dark:border-icoDarkMode-anthracite': withBorder,
                    'h-1.5 w-1.5': !withBorder
                },
                className
            )}
            {...restProps}
        />
    );
};

export type SettingsMenuUserAvatar = ComponentPropsWithoutRef<'button'> & {
    selected?: boolean;
    open?: boolean;
};

const UserAvatar = ({ selected = false, open }: SettingsMenuUserAvatar) => {
    const { user } = useUser();
    return (
        <button className="relative focus:rounded-md" data-testid="menu-settings-btn">
            <Avatar
                className="cursor-pointer align-top"
                fallback={
                    user?.first_name && user?.last_name
                        ? user.first_name.charAt(0) + user.last_name.charAt(0)
                        : ''
                }
                radius="rounded-md"
                url={user?.settings?.avatar ?? ''}
                selected={selected || open}
            />
            {user?.trial ? (
                <SubscriptionGuide
                    withBorder={true}
                    className="absolute -right-1 -top-1"
                    data-testid="subscription-guide-button"
                />
            ) : null}
        </button>
    );
};

const Panel = () => {
    const { user } = useUser();
    const { pathname } = useRouter();
    const { t } = useTranslation('commons');
    const { logout } = useLogout();
    const { hasAcl } = useAcl();
    const { lang } = useTranslation();

    const urlWithoutParams = pathname.split('?')[0];

    const shouldBeDisplayed = (item: SettingsMenuEntryType): boolean => {
        if (item?.shouldBeDisplayed) {
            return hasAcl(item?.shouldBeDisplayed);
        }
        return true;
    };

    return (
        <>
            <div className="flex flex-col items-center p-5" data-testid="menu-settings-header">
                <Avatar
                    fallback={
                        user?.first_name && user?.last_name
                            ? user.first_name.charAt(0) + user.last_name.charAt(0)
                            : ''
                    }
                    radius="rounded-md"
                    url={user?.settings?.avatar ?? ''}
                    className="mb-3"
                />
                <p className="w-full truncate text-center text-sm">
                    {`${user?.first_name ?? ''} ${user?.last_name ?? ''}`}
                </p>
                {ROLES[user?.acl_role]?.label ? (
                    <div className="mb-3 mt-1 w-fit">
                        <ActionTag variant="role" role={user.acl_role}>
                            <span>{t(ROLES[user?.acl_role]?.label)}</span>
                        </ActionTag>
                    </div>
                ) : null}
                {hasAcl(SOCIAL_PROFILES_RIGHT.create) ? (
                    <LinkBase
                        href={{
                            pathname: SOCIAL_PROFILES,
                            query: {
                                [SOCIAL_PROFILES_ACTION_PARAM.NAME]:
                                    SOCIAL_PROFILES_ACTION_PARAM.VALUES.ADD_SOCIAL_PROFILES
                            }
                        }}
                        className="w-full">
                        <Button
                            size="xs"
                            variant="noBackground"
                            icon="plus"
                            className="w-full !border">
                            {t('menu.add_social_profile')}
                        </Button>
                    </LinkBase>
                ) : null}
            </div>
            <div className="space-y-3.5">
                {user?.trial ? (
                    <MenuPopover.Item>
                        <LinkBase
                            href={{
                                pathname: SUBSCRIPTION_INVOICES,
                                query: { step: CHANGE_YOUR_PLAN }
                            }}
                            className="flex items-center text-icoGreen">
                            <Icon icon="arrow-top" className="text-base text-icoGreen" />
                            <span className="px-3">{t('subscribe_now')}</span>
                            <SubscriptionGuide data-testid="subscription-guide-panel" />
                        </LinkBase>
                    </MenuPopover.Item>
                ) : null}
                {settingsMenu.map((item, index) => {
                    if (item.name === 'separator_content') {
                        return <MenuPopover.Separator key={index} />;
                    }

                    if (!shouldBeDisplayed(item)) {
                        return null;
                    }

                    if (item.acl) {
                        if (item.acl.roles) {
                            if (!item.acl.roles.includes(user.acl_role)) {
                                return null;
                            }
                        }
                    }

                    const isActive =
                        item.link === urlWithoutParams ||
                        (item?.urlActiveMenu && item.urlActiveMenu.includes(urlWithoutParams));

                    return (
                        <MenuPopover.Item key={item.name}>
                            <LinkBase
                                href={item?.link?.replace(
                                    '{lang}',
                                    lang === 'en' ? '/' : `/${lang}/`
                                )}
                                target={item.target_blank ? '_blank' : ''}
                                rel={item.target_blank ? 'noopener noreferrer' : null}
                                className={clsx(
                                    'flex transition duration-300 ease-in-out hover:text-icoBlue',
                                    {
                                        'text-icoBlue': isActive
                                    }
                                )}>
                                <Icon icon={item.icon} className="text-base text-icoBlue" />
                                <span className="px-3">{t(item.name)}</span>
                            </LinkBase>
                        </MenuPopover.Item>
                    );
                })}
                <MenuPopover.Separator />
                <MenuPopover.Item className="pb-5">
                    <button
                        className="flex w-full transition duration-300 ease-in-out hover:text-icoBlue"
                        onClick={logout}>
                        <Icon icon={'logout'} className="text-base text-icoBlue" />
                        <span className="px-3">{t('logout')}</span>
                    </button>
                </MenuPopover.Item>
            </div>
        </>
    );
};

const popperOptions = {
    placement: 'right-end',
    modifiers: [
        {
            name: 'offset',
            options: {
                offset: [20, 35]
            }
        }
    ]
};

const MenuSettings = () => {
    const { pathname } = useRouter();

    const isActive =
        matchSorter(settingsMenu, pathname.split('?')[0], {
            keys: [
                { threshold: matchSorter.rankings.EQUAL, key: 'link' },
                { threshold: matchSorter.rankings.EQUAL, key: 'urlActiveMenu' }
            ]
        }).length > 0;

    return (
        <MenuPopover options={popperOptions} panel={<MenuSettings.Panel />}>
            <UserAvatar selected={isActive} />
        </MenuPopover>
    );
};

MenuSettings.Panel = Panel;

export default MenuSettings;
